import { type ListingButtonsState, MAX_LISTING_BUTTONS } from '@pulsify/shared/dto'
import { Loader2 } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { useCurrentBusiness } from '~/entities/business/model/use-business'
import { useListing } from '~/entities/listing/model/use-listings'
import {
  useListingButtons,
  useUpdateListingButtons,
} from '~/entities/listing-buttons/model/use-listing-buttons'
import * as m from '~/paraglide/messages'
import { getErrorMessageFromError } from '~/shared/lib/api-errors'
import type { Listing } from '~/shared/types'
import { Button } from '~/shared/ui/button'
import { SegmentedControl } from '~/shared/ui/segmented-control'
import { Skeleton } from '~/shared/ui/skeleton'
import {
  type ButtonsDraft,
  draftFromState,
  isButtonsDraftDirty,
  previewButtons,
  toButtonsRequest,
} from '../lib/button-chooser'
import { ButtonChooserList } from './button-chooser-list'
import { BUTTON_LABELS } from './button-kinds'
import { PhonePreview } from './phone-preview'

interface ButtonsEditorProps {
  businessId: string
  listing: Listing
  state: ListingButtonsState
}

function ButtonsEditor({ businessId, listing, state }: ButtonsEditorProps) {
  const [draft, setDraft] = useState<ButtonsDraft>(() => draftFromState(state))
  const update = useUpdateListingButtons(businessId)
  const shown = previewButtons(draft, state.available)
  const automatic = previewButtons({ mode: 'auto', chosen: [] }, state.available)
  const dirty = isButtonsDraftDirty(draft, state)

  async function save() {
    try {
      const saved = await update.mutateAsync({ listingId: listing.id, ...toButtonsRequest(draft) })
      setDraft(draftFromState(saved))
      toast.success(m.listingButtonsSaved())
    } catch (error) {
      toast.error(getErrorMessageFromError(error))
    }
  }

  return (
    <div className="mt-6 grid gap-8 lg:grid-cols-[320px_minmax(0,1fr)]">
      <PhonePreview
        name={listing.name}
        coverImageUrl={listing.coverImageUrl ?? listing.images[0]?.url ?? null}
        city={listing.location.city}
        buttons={shown}
      />
      <div className="flex min-w-0 flex-col gap-5">
        <SegmentedControl
          label={m.listingButtonsModeLabel()}
          value={draft.mode}
          options={[
            { value: 'auto', label: m.listingButtonsModeAuto() },
            { value: 'own', label: m.listingButtonsModeOwn() },
          ]}
          onChange={(mode) => setDraft({ ...draft, mode })}
        />
        {draft.mode === 'auto' ? (
          <p className="text-muted-foreground text-sm">
            {m.listingButtonsAutoExplanation({
              buttons: automatic.map((kind) => BUTTON_LABELS[kind]()).join(', '),
            })}
          </p>
        ) : (
          <div className="flex flex-col gap-3">
            <div className="flex items-baseline justify-between gap-3">
              <p className="text-muted-foreground text-sm">{m.listingButtonsOwnExplanation()}</p>
              <p className="shrink-0 text-muted-foreground text-xs tabular-nums">
                {m.listingButtonsCount({ count: draft.chosen.length, max: MAX_LISTING_BUTTONS })}
              </p>
            </div>
            <ButtonChooserList
              listingId={listing.id}
              chosen={draft.chosen}
              available={state.available}
              disabled={update.isPending}
              onChange={(chosen) => setDraft({ ...draft, chosen })}
            />
            {shown.length === 0 && (
              <p className="text-brand-subtle-foreground text-sm">
                {m.listingButtonsNoneVisible()}
              </p>
            )}
          </div>
        )}
        <div className="flex flex-col-reverse gap-3 border-t pt-5 sm:flex-row sm:items-center sm:justify-between">
          <p className="min-h-5 text-muted-foreground text-sm">
            {dirty ? m.listingButtonsUnsaved() : ''}
          </p>
          <Button type="button" disabled={!dirty || update.isPending} onClick={save}>
            {update.isPending && <Loader2 className="animate-spin" />}
            {update.isPending ? m.saving() : m.save()}
          </Button>
        </div>
      </div>
    </div>
  )
}

export function ListingButtonsSection({ listingId }: { listingId: string }) {
  const { data: business } = useCurrentBusiness()
  const businessId = business?.id ?? ''
  const { data: listing } = useListing(businessId, listingId)
  const buttons = useListingButtons(businessId, listingId)

  if (buttons.isError) {
    return (
      <div className="flex flex-col items-start gap-3">
        <p className="text-muted-foreground text-sm">{m.listingButtonsLoadError()}</p>
        <Button type="button" variant="outline" size="sm" onClick={() => buttons.refetch()}>
          {m.tryAgain()}
        </Button>
      </div>
    )
  }
  if (!listing || !buttons.data) return <Skeleton className="h-[34rem] w-full rounded-xl" />

  return (
    <div>
      <p className="max-w-prose text-muted-foreground text-sm">{m.listingButtonsDescription()}</p>
      <ButtonsEditor businessId={businessId} listing={listing} state={buttons.data} />
    </div>
  )
}
